/**
 * DEC-1450: "has this browser signed in to DECO before?" — the one signal the
 * beta gate can read on a cold return.
 *
 * Why it exists: a returning consumer whose deco_beta cookie lapsed (90-day
 * TTL) opens a bookmark, a typed URL, or an old email link and App.js renders
 * BetaAccessGate — the waitlist marketing page — before the route tree. The
 * only evidence they had been here was a stale `localStorage.token`, and the
 * api-client 401 interceptor deletes that before the gate ever mounts, so the
 * gate had nothing to read. One pilot user re-joined the waitlist from inside
 * his own site URL.
 *
 * What it stores: a boolean, never the email. A shared device must not greet
 * the next person with the previous user's address, and the sign-in modal's
 * lookup needs a typed identifier anyway. Written at every successful session
 * (AuthContext) and at the moment a held token is cleared (the 401
 * interceptor, logout) — i.e. whenever we KNOW a session existed here.
 * Never cleared by the app; clearing site data is the user's reset.
 *
 * Every accessor is try/catch'd: localStorage throws in private/blocked
 * contexts, and the gate must still render.
 */

export const RETURNING_BROWSER_KEY = 'deco_returning';

export function markReturningBrowser() {
  try {
    localStorage.setItem(RETURNING_BROWSER_KEY, '1');
  } catch (e) { /* storage blocked — the gate degrades to first-time copy */ }
}

// Residue a prior session leaves behind that the app can read SYNCHRONOUSLY at
// gate mount: a held token (valid or stale — the 401 that would clear it is
// still in flight when the gate first renders) and the org-context key that
// only an authenticated session ever writes. Either is proof enough, and it
// is what catches browsers that signed in BEFORE the marker existed.
const SESSION_RESIDUE_KEYS = ['token', 'deco_active_org'];

export function isReturningBrowser() {
  try {
    if (localStorage.getItem(RETURNING_BROWSER_KEY) === '1') return true;
    if (SESSION_RESIDUE_KEYS.some((k) => !!localStorage.getItem(k))) {
      // Promote the residue to the durable marker so the signal survives the
      // residue being cleared a moment later.
      markReturningBrowser();
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}
